import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import React, { useState } from "react";
import { Player } from "../../interfaces";

interface X01GameProps {
  playerOne: Player;
  playerTwo: Player;
}

const X01Game: React.FC<X01GameProps> = ({ playerOne, playerTwo }) => {
  const [scores, setScores] = useState<{ [key: string]: number }>({
    [playerOne.id]: 501,
    [playerTwo.id]: 501,
  });
  const [currentPlayer, setCurrentPlayer] = useState(playerOne.id);
  const [input, setInput] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [throws, setThrows] = useState<
    {
      p1Score: number | null;
      p1Remainder: number;
      p2Remainder: number;
      p2Score: number | null;
    }[]
  >(() => {
    const saved = localStorage.getItem("x01_throws");
    if (saved) return JSON.parse(saved);
    // Initial row: both at 501, no throws yet
    return [];
  });

  React.useEffect(() => {
    localStorage.setItem("x01_throws", JSON.stringify(throws));
  }, [throws]);

  const handleScoreSubmit = () => {
    const value = parseInt(input, 10);
    if (isNaN(value) || value < 0 || value > 180) {
      setError("Bitte gültige Punktzahl (0-180) eingeben.");
      return;
    }
    setError(null);

    setScores((prev) => {
      const newScore = Math.max(0, prev[currentPlayer] - value);
      return { ...prev, [currentPlayer]: newScore };
    });

    setThrows((prev) => {
      let last =
        prev.length > 0
          ? prev[prev.length - 1]
          : {
              p1Score: null,
              p1Remainder: 501,
              p2Score: null,
              p2Remainder: 501,
            };
      let newRow = { ...last };

      if (currentPlayer === playerOne.id) {
        // If last row is incomplete (p1 turn), fill p1Score and p1Remainder
        if (!prev.length || (last.p1Score !== null && last.p2Score !== null)) {
          // Start new row
          newRow = {
            p1Score: value,
            p1Remainder: Math.max(0, last.p1Remainder - value),
            p2Score: null,
            p2Remainder: last.p2Remainder,
          };
          return [...prev, newRow];
        } else {
          // Fill p1Score in last row
          newRow = {
            ...last,
            p1Score: value,
            p1Remainder: Math.max(0, last.p1Remainder - value),
          };
          return [...prev.slice(0, -1), newRow];
        }
      } else {
        // If last row is incomplete (p2 turn), fill p2Score and p2Remainder
        if (!prev.length || (last.p2Score !== null && last.p1Score !== null)) {
          // Start new row
          newRow = {
            p1Score: null,
            p1Remainder: last.p1Remainder,
            p2Score: value,
            p2Remainder: Math.max(0, last.p2Remainder - value),
          };
          return [...prev, newRow];
        } else {
          // Fill p2Score in last row
          newRow = {
            ...last,
            p2Score: value,
            p2Remainder: Math.max(0, last.p2Remainder - value),
          };
          return [...prev.slice(0, -1), newRow];
        }
      }
    });

    setInput("");
    setCurrentPlayer((prev) =>
      prev === playerOne.id ? playerTwo.id : playerOne.id
    );
  };

  return (
    <Box
      sx={{
        minHeight: "70vh",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        px: 2,
      }}
    >
      <Typography
        variant="h4"
        sx={{ mb: 2, fontWeight: 700, textAlign: "center" }}
      >
        501 Darts Match
      </Typography>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={4}
        sx={{ mb: 4, width: "100%", justifyContent: "center" }}
      >
        <Box sx={{ textAlign: "center" }}>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 600,
              color:
                currentPlayer === playerOne.id ? "primary.main" : undefined,
            }}
          >
            {playerOne.name}
          </Typography>
          <Typography variant="h3" sx={{ fontWeight: 700 }}>
            {scores[playerOne.id]}
          </Typography>
        </Box>
        <Box sx={{ textAlign: "center" }}>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 600,
              color:
                currentPlayer === playerTwo.id ? "primary.main" : undefined,
            }}
          >
            {playerTwo.name}
          </Typography>
          <Typography variant="h3" sx={{ fontWeight: 700 }}>
            {scores[playerTwo.id]}
          </Typography>
        </Box>
      </Stack>
      <Box
        sx={{ width: "100%", maxWidth: 340, mx: "auto", textAlign: "center" }}
      >
        <Typography sx={{ mb: 1 }}>
          {currentPlayer === playerOne.id ? playerOne.name : playerTwo.name} ist
          am Zug
        </Typography>
        <TextField
          type="number"
          label="Punkte dieser Aufnahme"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") handleScoreSubmit();
          }}
          inputProps={{
            min: 0,
            max: 180,
            inputMode: "numeric",
            pattern: "[0-9]*",
          }}
          sx={{ mb: 2, width: "100%" }}
          error={!!error}
          helperText={error}
        />
        <Button
          variant="contained"
          color="primary"
          fullWidth
          size="large"
          sx={{ fontWeight: 700, py: 1.5, fontSize: "1.1rem" }}
          onClick={handleScoreSubmit}
        >
          Punkte eintragen
        </Button>
      </Box>
      <Box sx={{ width: "100%", maxWidth: 600, mx: "auto", mt: 4 }}>
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell align="center">{playerOne.name} Wurf</TableCell>
                <TableCell align="center">{playerOne.name} Rest</TableCell>
                <TableCell align="center">|</TableCell>
                <TableCell align="center">{playerTwo.name} Rest</TableCell>
                <TableCell align="center">{playerTwo.name} Wurf</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {throws.map((row, idx) => (
                <TableRow key={idx}>
                  <TableCell align="center">
                    {row.p1Score !== null ? row.p1Score : ""}
                  </TableCell>
                  <TableCell align="center">{row.p1Remainder}</TableCell>
                  <TableCell align="center">|</TableCell>
                  <TableCell align="center">{row.p2Remainder}</TableCell>
                  <TableCell align="center">
                    {row.p2Score !== null ? row.p2Score : ""}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    </Box>
  );
};

export default X01Game;
